/*
 * @Description: 跳转至订单页面
 * @Date: 2025-07-21 17:47:10
 */
import appStore from '@/store';
import { storeToRefs } from 'pinia';
import router from '@/router';
import {
  fkAspPayedOrderMap,
  fkComprehensivePayedOrderMap,
  fkSupplierPayedOrderMap
} from '@data-source/order/apis';

type RouterMethod = 'push' | 'resolve' | 'open';

const getUserFncode = () => {
  const { info } = storeToRefs(appStore.useUserStoreSetup);
  const functionCode = info.value.functionCode;
  return {
    functionCode,
    isAsp: functionCode === 2,
    isSupplier: functionCode === 3,
    isGy: functionCode === 1
  };
};

const getOrderInfo = async orderNo => {
  // 使用Promise.any并行尝试三个映射API，只要有一个成功就继续。理论上只会有一个接口成功，多个成功了就是后端问题
  const { data } = await Promise.any([
    fkAspPayedOrderMap({ orderNo }, { isShowErrorMessage: false }),
    fkSupplierPayedOrderMap({ orderNo }, { isShowErrorMessage: false }),
    fkComprehensivePayedOrderMap({ orderNo }, { isShowErrorMessage: false })
  ]);
  return data;
};
const routeFn = (payload, method: RouterMethod = 'push') => {
  if (method === 'push') {
    router.push(payload);
    return null;
  }
  if (method === 'resolve') {
    const routeInfo = router.resolve(payload);
    return routeInfo;
  }
  if (method === 'open') {
    const routeInfo = router.resolve(payload);
    window.open(routeInfo.href, '_blank'); // 如果需要外部处理，再添加method类型
    return routeInfo;
  }
  return null;
};

/**
 * @description: 跳转到已结算订单详情
 * @params {string} method 路由跳转方式 resolve获取路由信息，window.open打开。push 内部直接跳转。open与resolve一样，内部直接window.open
 */
export const useJumpToOrderDetail = async (
  orderNo: string,
  option: {
    method: RouterMethod;
  } = {
    method: 'push'
  }
) => {
  const { isSupplier, isAsp } = getUserFncode();
  const {
    orderType,
    projectStructureType,
    id,
    isVirtualIndustry,
    sceneType,
    industryId,
    taxPattern
  } = await getOrderInfo(orderNo);

  if (isAsp) {
    const isSupplierOrder = projectStructureType === 1;
    const path = isSupplierOrder
      ? '/main/asp/finished-order/detail-b'
      : '/main/asp/finished-order/detail-c';
    // 查看已完结订单明细
    if (orderType === 1) {
      //项目费用查看明细
      return routeFn(
        {
          path,
          query: {
            aspOrderId: id,
            sceneType,
            projectStructureType,
            isEdit: 0
          }
        },
        option.method
      );
    } else {
      // 商务/平台服务费查看明细
      return routeFn(
        {
          path: '/main/asp/finished-order/detail-s',
          query: {
            orderId: id,
            orderType,
            sceneType,
            gysx: industryId && +industryId > 0 ? +!isVirtualIndustry : 0,
            taxPattern
          }
        },
        option.method
      );
    }
  }
  if (isSupplier) {
    if (orderType === 1) {
      //项目服务费订单
      return routeFn(
        {
          path: '/main/supplier/order/detail',
          query: {
            id
          }
        },
        option.method
      );
    } else {
      // 综合服务费订单
      return routeFn(
        {
          path: '/main/supplier/comprehensive-order/detail',
          query: {
            id
          }
        },
        option.method
      );
    }
  }
};

/**
 * @description: 跳转到订单列表
 */
export const useJumpToFinishedOrderList = async (
  orderNo: string,
  option: {
    pageName: 'unfinished' | 'finished';
    method: RouterMethod;
  } = {
    pageName: 'finished',
    method: 'push'
  }
) => {
  const { isAsp, isSupplier } = getUserFncode();
  const { orderType } = await getOrderInfo(orderNo);
  if (isAsp) {
    return routeFn(
      {
        path: '/main/asp/order',
        query: {
          orderNo,
          pageName: option.pageName
        }
      },
      option.method
    );
  }
  if (isSupplier) {
    // 项目服务费订单
    if (orderType === 1) {
      return routeFn(
        {
          path: '/main/supplier/order',
          query: {
            orderNo,
            pageName: option.pageName
          }
        },
        option.method
      );
    }
    // 综合服务费订单，对应接口无orderType字段
    else {
      return routeFn(
        {
          path: '/main/supplier/comprehensive-order',
          query: {
            orderNo
          }
        },
        option.method
      );
    }
  }
};
